import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Package, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { money, langName } from "@/lib/constants";
import { useLang } from "@/i18n";
import { Panel, Empty, RowSearch } from "@/pages/shared";

const LANGS = [["nl", "Nederlands"], ["fr", "Français"], ["de", "Deutsch"], ["no", "Norsk"], ["es", "Español"], ["en", "English"]];

const blank = () => ({ code: "", names: {}, pricing: "day", price: "", vat_rate: 21, quantity: 1, deposit: 0, notes: "", active: true });

export default function AdminRentals() {
  const { t, lang } = useLang();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(blank());
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  // Vult de andere talen met AI op basis van de Nederlandse benaming
  const aiTranslate = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/translate", {
        text: form.names.nl, source: "nl", targets: LANGS.map(([c]) => c).filter((c) => c !== "nl"),
        context: "korte benaming van een verhuurartikel op een camping",
      });
      setForm((f) => ({ ...f, names: { ...f.names, ...data } }));
      toast.success(t("ai_translated"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const load = async () => {
    try { setRows((await api.get("/rental-items")).data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => !s || r.code.toLowerCase().includes(s) || Object.values(r.names || {}).some((v) => (v || "").toLowerCase().includes(s)));
  }, [rows, q]);

  const save = async () => {
    setBusy(true);
    const body = { ...form, price: Number(form.price) || 0, vat_rate: Number(form.vat_rate) || 0, quantity: Number(form.quantity) || 1, deposit: Number(form.deposit) || 0 };
    try {
      if (editId) await api.put(`/rental-items/${editId}`, body);
      else await api.post("/rental-items", body);
      setOpen(false); await load(); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/rental-items/${id}`); await load(); toast.success(t("deleted")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Panel
      title={`${t("admin_rentals")} (${visible.length}${q ? `/${rows.length}` : ""})`}
      testid="rentals-panel"
      action={
        <div className="flex items-center gap-2">
          <RowSearch value={q} onChange={setQ} placeholder={t("art_code")} testid="rentals-search" />
          <Button onClick={() => { setForm(blank()); setEditId(null); setOpen(true); }} data-testid="new-rental-item-button">
            <Plus className="h-4 w-4 mr-1" />{t("new_rental_item")}
          </Button>
        </div>
      }
    >
      <div className="px-5 pt-4 text-sm text-muted-foreground">{t("admin_rentals_desc")}</div>
      {visible.length === 0 ? <Empty /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("art_code")}</TableHead>
              <TableHead>{t("art_name")}</TableHead>
              <TableHead>{t("rental_pricing")}</TableHead>
              <TableHead className="text-right">{t("art_price")}</TableHead>
              <TableHead className="text-right">{t("rental_deposit")}</TableHead>
              <TableHead className="text-right">{t("rental_qty_owned")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((r) => (
              <TableRow key={r.id} data-testid={`rental-item-row-${r.id}`} className={r.active === false ? "opacity-50" : ""}>
                <TableCell className="font-mono text-xs font-bold"><Package className="inline h-3.5 w-3.5 mr-1.5 text-sky-600" />{r.code}</TableCell>
                <TableCell className="font-medium text-foreground">{r.names?.nl || r.names?.en}</TableCell>
                <TableCell className="text-muted-foreground">{t(`pricing_${r.pricing}`)}</TableCell>
                <TableCell className="text-right tabular-nums">{money(r.price)}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">{r.deposit ? money(r.deposit) : "—"}</TableCell>
                <TableCell className="text-right tabular-nums">{r.quantity}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => { setForm({ ...blank(), ...r }); setEditId(r.id); setOpen(true); }} data-testid={`edit-rental-item-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(r.id)} data-testid={`delete-rental-item-${r.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-xl" data-testid="rental-item-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? t("edit") : t("new_rental_item")}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-auto cb-scroll pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">{t("art_code")}</Label>
                <Input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="rental-code" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("rental_pricing")}</Label>
                <Select value={form.pricing} onValueChange={(v) => setForm({ ...form, pricing: v })}>
                  <SelectTrigger data-testid="rental-pricing"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">{t("pricing_day")}</SelectItem>
                    <SelectItem value="week">{t("pricing_week")}</SelectItem>
                    <SelectItem value="stay">{t("pricing_stay")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label className="text-xs">{t("art_price")}</Label>
                <Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="rental-price" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("art_vat")}</Label>
                <Input type="number" min="0" step="1" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} data-testid="rental-vat" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("rental_qty_owned")}</Label>
                <Input type="number" min="1" step="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="rental-quantity" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("rental_deposit")}</Label>
                <Input type="number" min="0" step="0.01" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} data-testid="rental-deposit" /></div>
            </div>
            <div className="flex items-center justify-between gap-2 pt-1">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("rental_names")}</p>
              <Button type="button" variant="outline" size="sm" className="h-8 rounded-full" onClick={aiTranslate}
                disabled={aiBusy || !(form.names?.nl || "").trim()} data-testid="ai-translate-button">
                <Sparkles className={`h-4 w-4 mr-1 ${aiBusy ? "animate-pulse" : ""}`} />{aiBusy ? t("ai_translating") : t("ai_translate")}
              </Button>
            </div>
            {LANGS.map(([code]) => (
              <div key={code} className="space-y-1">
                <Label className="text-xs">{langName(code, lang)}</Label>
                <Input value={form.names?.[code] || ""} data-testid={`rental-name-${code}`}
                  onChange={(e) => setForm({ ...form, names: { ...form.names, [code]: e.target.value } })} />
              </div>
            ))}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="accent-sky-600" checked={form.active !== false}
                onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="rental-active" />
              {t("active")}
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} disabled={busy || !form.code.trim()} data-testid="save-rental-item-button">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
